import {
  Component, Input, Output, EventEmitter,
  forwardRef, signal, computed, input, viewChild,
  ChangeDetectionStrategy, ViewEncapsulation,
  ElementRef, inject, OnDestroy, afterNextRender, ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CheckboxComponent } from '../checkbox/checkbox.component';

export interface SelectOption {
  id:        any;
  label:     string;
  disabled?: boolean;
}

export type SelectSize = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';

@Component({
  selector:        'app-select',
  standalone:      true,
  imports:         [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, CheckboxComponent],
  templateUrl:     './select.component.html',
  styleUrls:       ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation:   ViewEncapsulation.None,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SelectComponent), multi: true }],
})
export class SelectComponent implements ControlValueAccessor, OnDestroy {

  /**
   * Signal input : les options arrivent souvent après la valeur (chargement
   * asynchrone). Sans réactivité ici, `selectedOption` resterait figé sur sa
   * première évaluation — liste vide — et le champ afficherait le placeholder
   * alors qu'une valeur est bien sélectionnée.
   */
  options  = input<SelectOption[]>([]);
  multiple = input<boolean>(false);

  /** Active un champ de recherche dans la liste, avec filtrage par libellé. */
  searchable = input<boolean>(false);

  @Input() placeholder = 'OPERATIONS.SELECT';
  @Input() disabled    = false;
  @Input() size:         SelectSize = 'medium';
  @Input() addIconLeft  = false;
  @Input() addIconRight = false;
  @Input() withCheckboxes = false;
  @Input() clearable = false;

  /**
   * Action optionnelle épinglée sous la liste (ex. « Nouvel explosif »). Elle
   * reste masquée tant que le libellé est vide : les appelants existants ne
   * voient aucun changement.
   */
  @Input() footerLabel = '';

  @Output() selectionChange = new EventEmitter<SelectOption>();
  @Output() selectionsChange = new EventEmitter<SelectOption[]>();
  @Output() footerClicked = new EventEmitter<void>();
  @Output() cleared = new EventEmitter<void>();

  private readonly _el  = inject(ElementRef<HTMLElement>);
  private readonly _cdr = inject(ChangeDetectorRef);

  readonly isOpen     = signal(false);
  readonly isFocused  = signal(false);
  readonly selectedId = signal<any>(null);
  readonly selectedIds = signal<any[]>([]);

  readonly selectedOption = computed(() => this.options().find(o => o.id === this.selectedId()) ?? null);
  readonly selectedOptions = computed(() => {
    const ids = this.selectedIds();
    return this.options().filter(o => ids.includes(o.id));
  });
  readonly selectedCount = computed(() => this.selectedOptions().length);
  readonly displayLabel   = computed(() => {
    if (this.multiple()) {
      const labels = this.selectedOptions().map(opt => opt.label);
      return labels.join(', ');
    }
    return this.selectedOption()?.label ?? '';
  });

  readonly hasValue = computed(() => this.multiple() ? this.selectedCount() > 0 : this.selectedOption() !== null);

  readonly searchTerm = signal('');

  readonly showSelectionAsPlaceholder = computed(() => this.multiple() && this.isOpen() && !!this.displayLabel());
  /** Texte affiché dans le champ : la recherche tant qu'on tape, sinon le libellé sélectionné. */
  readonly inputTerm = computed(() => this.isOpen() ? this.searchTerm() : this.displayLabel());

  /** Options filtrées par le terme de recherche (insensible à la casse). */
  readonly visibleOptions = computed<SelectOption[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (!this.searchable() || !term) return this.options();
    return this.options().filter(opt => opt.label.toLowerCase().includes(term));
  });

  private readonly _input = viewChild<ElementRef<HTMLInputElement>>('selectInput');

  readonly dropdownStyle = signal<Record<string, string>>({});

  // ── Fermeture au clic extérieur ───────────────────────────────────────────
  private readonly _onDocClick = (e: MouseEvent) => {
    if (!this._el.nativeElement.contains(e.target as Node)) {
      this.isOpen.set(false);
      this.searchTerm.set('');
    }
  };

  private readonly _onScroll = () => { if (this.isOpen()) this._updatePos(); };

  constructor() {
    afterNextRender(() => {
      document.addEventListener('click',  this._onDocClick, true);
      window.addEventListener('scroll',   this._onScroll,   true);
      window.addEventListener('resize',   this._onScroll);
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('click',  this._onDocClick, true);
    window.removeEventListener('scroll',   this._onScroll,   true);
    window.removeEventListener('resize',   this._onScroll);
  }

  // ── CVA ───────────────────────────────────────────────────────────────────
  private _onChange:  (v: any) => void = () => {};
  private _onTouched: () => void       = () => {};

  writeValue(val: any): void {
    if (this.multiple()) {
      this.selectedIds.set(Array.isArray(val) ? val : []);
      return;
    }
    this.selectedId.set(val ?? null);
  }
  registerOnChange(fn: (v: any) => void): void { this._onChange = fn; }
  registerOnTouched(fn: () => void): void      { this._onTouched = fn; }
  /**
   * `disabled` n'est pas un signal : sans `markForCheck`, un `enable()`/`disable()`
   * posé sur le contrôle après le premier rendu ne repeindrait pas le champ.
   */
  setDisabledState(d: boolean): void {
    this.disabled = d;
    if (d) this.isOpen.set(false);
    this._cdr.markForCheck();
  }

  // ── Interactions ──────────────────────────────────────────────────────────
  toggle(): void {
    if (this.disabled) return;
    if (!this.isOpen()) {
      this.searchTerm.set('');
      this._updatePos();
      this.isOpen.set(true);
      this._focusSearch();
    }
    else if (!this.searchable()) this.isOpen.set(false);
  }

  onSearchTerm(term: string): void {
    this.searchTerm.set(term);
  }

  private _focusSearch(): void {
    if (!this.searchable()) return;
    afterNextRender(() => this._input()?.nativeElement.focus());
  }

  private _updatePos(): void {
    const rect = this._el.nativeElement.getBoundingClientRect();

    // Un ancêtre transformé (ex. animation `transform` du popup) devient le
    // bloc conteneur des descendants `position: fixed`. On compense son offset
    // pour que les coordonnées viewport restent correctes.
    const origin = this._containingBlockOrigin();

    const gap = 12; // 4px d'écart + 0.5rem de marge
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const defaultMaxHeight = 15 * rem;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < defaultMaxHeight && spaceAbove > spaceBelow;
    const maxHeight = Math.max(Math.min(defaultMaxHeight, openUp ? spaceAbove : spaceBelow), 3 * rem);

    const position: Record<string, string> = openUp
      ? { bottom: `${origin.bottom - rect.top + 4}px`, 'margin-top': '0', 'margin-bottom': '0.5rem' }
      : { top: `${rect.bottom + 4 - origin.top}px` };

    this.dropdownStyle.set({
      ...position,
      left:         `${rect.left - origin.left}px`,
      width:        `${rect.width}px`,
      'max-height': `${maxHeight}px`,
    });
  }

  /**
   * Un ancêtre transformé (ex. animation `transform` d'un popup) devient le
   * bloc conteneur des descendants `position: fixed`. On compense son offset
   * pour que les coordonnées viewport du dropdown restent correctes.
   */
  private _containingBlockOrigin(): { top: number; left: number; bottom: number } {
    let node = this._el.nativeElement.parentElement;

    while (node) {
      const style = getComputedStyle(node);
      const creates = style.transform !== 'none'
        || style.perspective !== 'none'
        || style.filter !== 'none'
        || style.willChange.includes('transform')
        || style.willChange.includes('perspective')
        || style.willChange.includes('filter');

      if (creates) {
        const rect = node.getBoundingClientRect();
        return { top: rect.top, left: rect.left, bottom: rect.bottom };
      }
      node = node.parentElement;
    }

    return { top: 0, left: 0, bottom: window.innerHeight };
  }

  selectOption(opt: SelectOption): void {
    if (opt.disabled) return;
    if (this.multiple()) {
      this.toggleMultipleOption(opt);
      return;
    }
    this.selectedId.set(opt.id);
    this.searchTerm.set('');
    this._onChange(opt.id);
    this._onTouched();
    this.selectionChange.emit(opt);
    this.isOpen.set(false);
  }

  /** Le pied de liste est une action, pas une option : il referme sans toucher à la valeur. */
  clickFooter(): void {
    this.isOpen.set(false);
    this.footerClicked.emit();
  }

  isSelected(opt: SelectOption): boolean { return this.selectedId() === opt.id; }
  isSelectedInMulti(opt: SelectOption): boolean { return this.selectedIds().includes(opt.id); }

  toggleMultipleOption(opt: SelectOption, nextChecked?: boolean): void {
    if (opt.disabled) return;
    const current = this.selectedIds();
    const exists = current.includes(opt.id);
    const shouldAdd = nextChecked !== undefined ? nextChecked : !exists;
    const next = shouldAdd
      ? (exists ? current : [...current, opt.id])
      : current.filter(id => id !== opt.id);

    this.selectedIds.set(next);
    this._onChange(next);
    this._onTouched();
    const selected = this.options().filter(o => next.includes(o.id));
    this.selectionsChange.emit(selected);

    if (this.searchable() && this.searchTerm()) {
      this.searchTerm.set('');
      this._focusSearch();
    }
  }

  clear(event: Event): void {
    event.stopPropagation();
    if (this.disabled) return;
    this.searchTerm.set('');
    if (this.multiple()) {
      this.selectedIds.set([]);
      this._onChange([]);
      this.selectionsChange.emit([]);
    } else {
      this.selectedId.set(null);
      this._onChange(null);
    }
    this._onTouched();
    this.cleared.emit();
  }

  onFocus(): void { this.isFocused.set(true); }
  onBlur(): void  { this.isFocused.set(false); this._onTouched(); }
}